import { Icon, IconVariants, Modal, ModalSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";

import { Col, Row } from "@/components/customComponents";
import { cn } from "@/utils";

export interface SelectionItem<T = string> {
  value: T;
  label: string;
  description?: string;
  image?: string;
}

export interface SelectionModalProps<T = string> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modalTitle: string;
  selectedValue: T;
  items: SelectionItem<T>[];
  onSelectionChange: (value: T) => void;
  gridCols?: {
    mobile: number;
    desktop: number;
  };
  modalSize?: ModalSize;
  classNames?: {
    modal?: string;
    grid?: string;
    item?: string;
  };
  dataTestId?: string;
}

const SelectionItemComponent = <T,>({
  item,
  active,
  onSelect,
  showDescription = false,
  className,
}: {
  item: SelectionItem<T>;
  active: boolean;
  onSelect: (value: T) => void;
  showDescription?: boolean;
  className?: string;
}) => {
  return (
    <Row
      className={cn("gap-md justify-between p-md rounded-sm cursor-pointer hover:bg-gray-8", active && "bg-gray-9", className)}
      onClick={() => onSelect(item.value)}
    >
      {item.image && <img src={item.image} alt={item.label} className="w-12 h-12 rounded object-cover flex-shrink-0" />}
      <Col className="flex-1">
        <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>{item.label}</Text>
        {showDescription && item.description && <Text variant={TextVariants.LABEL_SMALL_REGULAR}>{item.description}</Text>}
      </Col>
      {active && <Icon icon={IconVariants.CHECK} />}
    </Row>
  );
};

export const SelectionModal = <T,>({
  open,
  onOpenChange,
  modalTitle,
  selectedValue,
  items,
  onSelectionChange,
  gridCols = { mobile: 1, desktop: 2 },
  modalSize = ModalSize.MEDIUM,
  classNames,
  dataTestId = "",
}: SelectionModalProps<T>) => {
  const hasDescriptions = items.some((item: SelectionItem<T>) => item.description);

  const handleSelectionChange = (value: T) => {
    onOpenChange(false);
    onSelectionChange(value);
  };

  const gridClasses = cn(`grid grid-cols-${gridCols.mobile} md:grid-cols-${gridCols.desktop} gap-sm mt-4`, classNames?.grid);

  return (
    <Modal open={open} size={modalSize} onOpenChange={onOpenChange} className={classNames?.modal}>
      <Text variant={TextVariants.HEADING_6_BOLD}>{modalTitle}</Text>
      <div className={gridClasses} data-testid={dataTestId}>
        {items.map((item: SelectionItem<T>) => (
          <SelectionItemComponent
            key={String(item.value)}
            item={item}
            active={item.value === selectedValue}
            onSelect={handleSelectionChange}
            showDescription={hasDescriptions}
            className={classNames?.item}
          />
        ))}
      </div>
    </Modal>
  );
};
